import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'

import { onCheckListRolePresence } from '@/app/utils/onCheckListRolePresence'
import { Loader, PageWrapper } from '@/shared'
import { routesConfig } from '@App/router'
import { BrowserLocalstorage, authState } from '@App/store'
import { type IUpdatePasswordDto } from '@App/types/authType'
import { routes } from '@Config/config'

import { ChangePasswordModal } from './ChangePasswordModal/ChangePasswordModal'
import style from './Header.module.scss'
import { User } from './User/User'

export const Header = observer(() => {
    const navigate = useNavigate()

    const [showChangePassword, setShowChangePassword] = useState(false)

    const accessToken = BrowserLocalstorage.getToken()

    useEffect(() => {
        if (!accessToken) {
            authState.onSetUserData(null)
            navigate(routes.AUTH)
        }
        if (accessToken && !authState.userData) {
            authState.onGetUserData()
        }
    }, [accessToken, navigate])

    const titledRoutes = routesConfig.filter(
        (route) =>
            authState.userData?.roles &&
            route.title &&
            onCheckListRolePresence(route.allowedRoles, authState.userData.roles)
    )

    const changePassword = useCallback((dto: IUpdatePasswordDto) => {
        authState.onChangeAdminPassword(dto, () => setShowChangePassword(false))
    }, [])

    const onAppLogout = useCallback(() => {
        authState.onSignOut(() => navigate(routes.AUTH))
    }, [navigate])

    return (
        <div className={style.container}>
            {showChangePassword && (
                <ChangePasswordModal
                    onChangePassword={changePassword}
                    show={showChangePassword}
                    onClose={() => setShowChangePassword(false)}
                />
            )}
            <PageWrapper>
                {authState.userData ? (
                    <div className={style.upperHeader}>
                        <div className={style.logoWrapper}>
                            <img
                                onClick={() => navigate('/')}
                                src={`${process.env.REACT_APP_API_URL}api/storage/logo.png`}
                                onError={({ currentTarget }) => {
                                    currentTarget.onerror = null
                                    currentTarget.src =
                                        'https://avatars.mds.yandex.net/i?id=d0c6a5c781cf8262ba1c22c4876c1429c58b4018-12509449-images-thumbs&n=13'
                                }}
                                alt='unknown'
                            />
                        </div>
                        <nav>
                            {titledRoutes.map(({ title, route }) => {
                                const routeStyle = classNames(style.route, style.routeStyle)
                                const activeRouteStyle = classNames(style.route, style.routeStyle, style.activeStyle)
                                return (
                                    <NavLink
                                        to={route}
                                        key={route}
                                        className={({ isActive }) => (isActive ? activeRouteStyle : routeStyle)}
                                    >
                                        {title}
                                    </NavLink>
                                )
                            })}
                        </nav>
                        <div className={style.user}>
                            <User
                                onOpenChangePasswordModal={() => setShowChangePassword(true)}
                                login={authState.userData.login}
                                roles={authState.userData.roles}
                                onLogout={onAppLogout}
                            />
                        </div>
                    </div>
                ) : (
                    <Loader className={style.loader} />
                )}
            </PageWrapper>
        </div>
    )
})
